import React, { useState, useCallback, KeyboardEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { Store } from 'types/store';
import { RLRVehicleInfoEnum, RLRProductCodesEnum, RLRProductType } from 'constants/journeys/loyaltyRewardsEnums';
import infoIconSvg from 'assets/images/button/info-blue.svg';
import documentsIcon from 'assets/images/loyaltyRewards/document.png';
import PopupModal from 'components/PopupModal/PopupModal';
import { setRLRJourneyReward } from 'actions/journeys/loyaltyRewardsActions';
import RegistrationSelect from './VehicleLookup/RegistrationSelect';
import styles from './ProductCard.module.scss';

interface ProductCardProps {
  product: RLRProductType;
  ipiDocumentUrl?: string | null;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, ipiDocumentUrl }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState<boolean>(false);
  const vehicleDisplayModality = useSelector((state: Store) => state.journeys.loyaltyRewards.vehicleDisplayModality);
  const selectedReward = useSelector((state: Store) => state.journeys.loyaltyRewards.data.selectedReward);
  const isSelected = selectedReward?.productCode === product.productCode;

  const showVehicleSearch = useCallback((): boolean => {
    const isBatteryOrTyreReplace = product.productCode === RLRProductCodesEnum.DRV_BATTERY || product.productCode === RLRProductCodesEnum.DRV_TYRE;
    switch (vehicleDisplayModality) {
      case (RLRVehicleInfoEnum.HasVerifiedExistingPolicyVehicle):
      case (RLRVehicleInfoEnum.AddNewPolicyVehicle):
      case (RLRVehicleInfoEnum.OptionallyChangeExistingPolicyVehicle): return isBatteryOrTyreReplace
      case (RLRVehicleInfoEnum.NoVehicleCapture):
      default: return false;
    }
  }, [vehicleDisplayModality, product.productCode]);

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      dispatch(setRLRJourneyReward(isSelected ? null : product));
    }
  };

  const handleClick = () => {
    dispatch(setRLRJourneyReward(isSelected ? null : product));
  };

  return (
    <div className={styles.productCard}>
      <h4 className={styles.title}>
        {product.title}
        <span className={styles.subTitle}>{product.subTitle}</span>
      </h4>
      <p className={styles.description}>{product.description}</p>
      {product.findOutMore && <button type="button" className={styles.findOutMore} onClick={() => setShowModal(true)}><img src={infoIconSvg} alt="info" height="18" width="18" className={styles.icon} /> <span>Find out more</span></button>}
      {showModal && product.findOutMore && (
        <PopupModal
          resultTitle={product.title}
          resultText={product.findOutMore}
          action={() => setShowModal(false)}
          displayDialogInMiddle
          innerCloseButton
          parseHtml
        />
      )}
      {ipiDocumentUrl && product.productCode === RLRProductCodesEnum.DRV_EUROPEAN && (
        <div className="row align-items-center pb-4">
          <div className="col-auto mx-0">
            <img src={documentsIcon} alt="doc" width="24px" />
          </div>
          <div className="col px-0">
            Please read the <a href={ipiDocumentUrl} target="_blank" rel="noopener noreferrer" className={styles.documentLink}>insurance product information document</a> before choosing this upgrade.
          </div>
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        className={clsx(styles.productSelectBtn, isSelected && styles.selected)}
        onClick={handleClick}
        onKeyUp={handleKeyUp}
      >
        <span className={clsx(styles.buttonTitle, isSelected && styles.selected)}>Add {product.title}</span>
        <span className={clsx(styles.buttonCost, isSelected && styles.selected)}>FREE</span>
        <p className={isSelected && showVehicleSearch() ? 'pb-2' : 'pb-0'}>Usually <b>£{product.usuallyPerYear}</b>/year</p>
        {isSelected && showVehicleSearch() && <RegistrationSelect product={product} />}
      </div>
    </div>
)
};

export default ProductCard;
